package com.heelion.farmafreelance.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import farmafreelance.composeapp.generated.resources.Res
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.ExperimentalResourceApi
import com.heelion.farmafreelance.agent.ContestoUtente
import com.heelion.farmafreelance.agent.ConversazioneFarmy
import com.heelion.farmafreelance.agent.TipoChiacchiera
import com.heelion.farmafreelance.agent.FarmaFreelanceAgent
import com.heelion.farmafreelance.agent.IntentClassifier
import com.heelion.farmafreelance.utils.StimaFiscale
import com.heelion.farmafreelance.agent.RispostaAgente
import com.heelion.farmafreelance.agent.RisponditoreDomande
import com.heelion.farmafreelance.agent.ResocontoPagamenti
import com.heelion.farmafreelance.agent.TipoDomanda
import com.heelion.farmafreelance.agent.EstrattorePromemoria
import com.heelion.farmafreelance.agent.ImpostazionePromemoria
import com.heelion.farmafreelance.agent.PreferenzePromemoria
import com.heelion.farmafreelance.agent.TipoPromemoria
import com.heelion.farmafreelance.push.rememberPromemoriaLocali
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Farmacia
import com.heelion.farmafreelance.models.Farmacista
import com.heelion.farmafreelance.models.StoricoEntry
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.Professionista
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.voice.FRASE_PROVA_VOCE
import com.heelion.farmafreelance.voice.VoiceSpeaker
import com.heelion.farmafreelance.voice.rememberVoiceSpeaker

/**
 * L'AI di FarmaFreelance vista dalla UI: un unico punto usato sia dall'assistente testuale sia dai
 * microfoni delle varie schermate. Capisce la frase con [FarmaFreelanceAgent] e, se è una domanda,
 * risponde con i dati dell'utente letti al momento della domanda da [contesto].
 */
class Assistente(
    private val agent: FarmaFreelanceAgent,
    /** Un solo motore vocale per tutta l'app (su Android ognuno avvia un motore TTS a parte). */
    val voce: VoiceSpeaker,
    /** Turni aperti delle controparti nella provincia data (null = tutta Italia); null se la rete non risponde. */
    private val caricaAnnunci: suspend (provincia: String?) -> List<AnnuncioTurno>?,
    /** Storico dei turni svolti (per i guadagni); null se la rete non risponde. */
    private val caricaStorico: suspend () -> List<StoricoEntry>? = { null },
    /** Turni o disponibilità pubblicati dall'utente ("il turno di sabato è coperto?"); null se la rete non risponde. */
    private val caricaMieiAnnunci: suspend () -> List<AnnuncioTurno>? = { null },
    /** Salva la voce scelta nel selettore ("" = automatica), per ritrovarla al prossimo avvio. */
    private val salvaVoce: (String) -> Unit = {},
    /** Promemoria di Farmy attivi (salvati nel ViewModel). */
    private val leggiPromemoria: () -> PreferenzePromemoria = { PreferenzePromemoria() },
    private val salvaPromemoria: (PreferenzePromemoria) -> Unit = {},
    /** true dove Farmy può programmare promemoria sul dispositivo (Android); sul web solo le push. */
    val promemoriaLocali: Boolean = false,
    /** Esegue le azioni nell'app (rispondere a proposte, preferiti...); null nelle anteprime. */
    val esecutore: EsecutoreAzioni? = null,
    private val contesto: () -> ContestoUtente
) {
    /** Cambia la voce di Farmy, la salva e la fa ascoltare con una frase di prova. */
    fun scegliVoce(id: String?) {
        voce.usaVoce(id)
        salvaVoce(id.orEmpty())
        voce.speak(FRASE_PROVA_VOCE)
    }

    fun elabora(testo: String): RispostaAgente = agent.elabora(testo, oggi())

    /** "ricordami ogni mattina i turni…": applica, salva e restituisce la conferma di Farmy. */
    fun impostaPromemoria(impostazione: ImpostazionePromemoria): String {
        val dopo = EstrattorePromemoria.applica(leggiPromemoria(), impostazione)
        if (impostazione.tipi.isNotEmpty()) salvaPromemoria(dopo)
        return EstrattorePromemoria.conferma(impostazione, dopo, promemoriaLocali)
    }

    fun preferenzePromemoria(): PreferenzePromemoria = leggiPromemoria()

    /** Dagli interruttori nel popup di Farmy. */
    fun cambiaPromemoria(tipo: TipoPromemoria, attivo: Boolean) {
        val p = leggiPromemoria()
        salvaPromemoria(p.copy(attivi = if (attivo) p.attivi + tipo else p.attivi - tipo))
    }

    /**
     * Sospende solo per le domande sui turni aperti (serve la rete); le altre rispondono subito
     * dai dati già caricati. I turni dei preferiti si cercano in tutta Italia, non solo in zona.
     */
    suspend fun rispondi(tipo: TipoDomanda, testo: String = ""): String {
        val ctx = contesto()
        val annunci = if (tipo.richiedeAnnunci) caricaAnnunci(RisponditoreDomande.provinciaAnnunci(tipo, ctx, testo)) else null
        val storico = if (tipo.richiedeStorico) caricaStorico() else null
        val mieiAnnunci = if (tipo.richiedeMieiAnnunci) caricaMieiAnnunci() else null
        return RisponditoreDomande.rispondi(tipo, ctx, oggi(), annunci = annunci, testo = testo, storico = storico, mieiAnnunci = mieiAnnunci)
    }

    /** Domande pronte da toccare all'apertura di Farmy, adatte a chi usa l'app e a cosa ha in sospeso. */
    fun suggerimentiIniziali(): List<String> = ConversazioneFarmy.iniziali(contesto(), impegniVicini = promemoria() != null)

    /** Dopo la risposta a [tipo]: la domanda che di solito viene dopo. */
    fun suggerimentiSeguito(tipo: TipoDomanda): List<String> = ConversazioneFarmy.seguito(tipo, contesto().ruolo, contesto().professioneCercata)

    /** Quando non ha capito [frase]: il messaggio gentile e le domande che forse intendeva. */
    fun nonCapito(frase: String): Pair<String, List<String>> {
        val ipotesi = agent.ipotesi(frase)
        return ConversazioneFarmy.nonCapito(dalModello = ipotesi.isNotEmpty()) to
            ConversazioneFarmy.suggerimentiNonCapito(frase, ipotesi, contesto().ruolo, contesto().professioneCercata,
                abbonamentiAttivi = com.heelion.farmafreelance.ABBONAMENTI_ATTIVI)
    }

    /** Saluti, grazie, "non so cosa chiederti": la risposta e, se servono, le domande da cui partire. */
    fun chiacchiera(tipo: TipoChiacchiera): Pair<String, List<String>> {
        val conDomande = tipo in setOf(TipoChiacchiera.SALUTO, TipoChiacchiera.SERVE_AIUTO, TipoChiacchiera.FRUSTRAZIONE, TipoChiacchiera.CHI_SEI)
        return ConversazioneFarmy.risposta(tipo) to if (conDomande) suggerimentiIniziali() else emptyList()
    }

    /** Impegni da ricordare nel saluto (turni dei prossimi 7 giorni, da confermare); null se nessuno. */
    fun promemoria(): String? = RisponditoreDomande.promemoria(contesto(), oggi())

    /** Professionista, primi 7 giorni del mese: pagamenti del mese scorso non ancora spuntati; null se niente. */
    suspend fun promemoriaPagamenti(): String? {
        if (contesto().ruolo?.isProfessionista != true || oggi().dayOfMonth > 7) return null
        val storico = caricaStorico() ?: return null
        return ResocontoPagamenti.promemoriaMeseScorso(storico, oggi())
    }

    private fun oggi(): LocalDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
}

/** Default (es. anteprime): solo regole, nessuna voce, nessun dato utente. [App] fornisce quello vero. */
val LocalAssistente = compositionLocalOf {
    Assistente(FarmaFreelanceAgent(), NessunaVoce, caricaAnnunci = { null }) { ContestoUtente.VUOTO }
}

private object NessunaVoce : VoiceSpeaker {
    override fun isAvailable() = false
    override fun speak(testo: String, accoda: Boolean) {}
    override fun stop() {}
}

/**
 * Carica il modello addestrato (composeResources/files/intent_model.json, generato da
 * agent/training/train.py) in background. Finché non è pronto, o se il caricamento fallisce,
 * l'assistente funziona con le sole regole, come prima.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
fun rememberAssistente(
    viewModel: AuthViewModel,
    /** Apre la scheda di una farmacia/un professionista ("apri il profilo della Farmacia Rossi"). */
    apriProfilo: (Professionista?, Farmacia?) -> Unit = { _, _ -> }
): Assistente {
    val esecutore = remember(viewModel) { EsecutoreAzioni(viewModel, apriProfilo) }
    val voce = rememberVoiceSpeaker()
    val promemoriaLocali = rememberPromemoriaLocali() != null
    // Voce salvata nelle impostazioni: disponibile solo dopo initStorage, quindi la si riapplica
    // quando cambia (all'avvio passa da "" al valore salvato).
    LaunchedEffect(voce, viewModel.voceFarmy) { voce.usaVoce(viewModel.voceFarmy.ifBlank { null }) }
    var classificatore by remember { mutableStateOf<IntentClassifier?>(null) }
    LaunchedEffect(Unit) {
        classificatore = try {
            withContext(Dispatchers.Default) {
                IntentClassifier.fromJson(Res.readBytes("files/intent_model.json").decodeToString())
            }
        } catch (e: Exception) {
            println("--- [ASSISTENTE] Modello non caricato, uso solo le regole: ${e.message} ---")
            null
        }
        println("--- [ASSISTENTE] Modello caricato: ${classificatore?.intenti?.size ?: 0} intenti ---")
    }
    return remember(classificatore, voce, promemoriaLocali) {
        Assistente(
            FarmaFreelanceAgent(classificatore), voce,
            caricaAnnunci = viewModel::annunciControparti,
            caricaStorico = viewModel::getStoricoOppureNull,
            caricaMieiAnnunci = { runCatching { viewModel.getAnnunciUtente() }.getOrNull() },
            salvaVoce = viewModel::updateVoceFarmy,
            leggiPromemoria = { viewModel.preferenzePromemoria },
            salvaPromemoria = viewModel::aggiornaPreferenzePromemoria,
            promemoriaLocali = promemoriaLocali,
            esecutore = esecutore
        ) {
            ContestoUtente(
                ruolo = viewModel.currentUserRole,
                matches = viewModel.currentMatches,
                notifiche = viewModel.notifications,
                abbonamento = viewModel.subscriptionStatus,
                abbonamentiAttivi = com.heelion.farmafreelance.ABBONAMENTI_ATTIVI,
                reputazione = viewModel.userReputation,
                preferiti = viewModel.currentPreferiti,
                zona = viewModel.zonaUtente,
                promemoria = viewModel.preferenzePromemoria,
                dettagliConfermati = viewModel.confirmedShiftsDetails.mapNotNull { a -> a.id?.let { it to a } }.toMap(),
                // Cassa del professionista: ENPAF per i farmacisti, ENPAPI per gli infermieri.
                percEnpaf = viewModel.currentUserInfermiere?.percEnpapi ?: viewModel.currentUserFarmacista?.percEnpaf ?: 2.0,
                cassa = if (viewModel.currentUserInfermiere != null) "ENPAPI" else "ENPAF",
                aliquotaImposta = viewModel.currentUserProfessionista?.let { StimaFiscale.aliquota(it.regimeFiscale, it.aliquotaForfettario) },
                posizione = viewModel.posizioneUtente,
                professioneCercata = if (viewModel.infermieriAttivi) viewModel.professioneVisibile else null
            )
        }
    }
}
